import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { signOut as safeSignOut } from "@/lib/auth";
import { setCurrentUserId } from "@/observability/log";
import { clearActing } from "@/lib/acting";

export type AuthState = {
  ready: boolean;
  loading: boolean;
  initializing: boolean; // <-- new flag for one-time "ready" gate
  user: any | null;
  profile: { 
    role?: string; 
    onboarding_completed?: boolean; 
    verification_status?: string; 
    verification_status_new?: string;
    profile_status?: string;
    full_name?: string 
  } | null;
  isAdminUser: boolean;
  email: string | null;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

// Module-scoped subscription guard to prevent duplicate subscriptions
let _authSubBound = false;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Note: React StrictMode intentionally double-mounts components in development
  // This is normal behavior and not a problem

  const [state, setState] = useState<AuthState>({
    ready: false,
    loading: true,
    initializing: true, // <-- start as initializing
    user: null,
    profile: null,
    isAdminUser: false,
    email: null,
    signOut: async () => {
      clearActing(); // Reset to default role on sign-out
      const path = await safeSignOut(false);
      if (path) {
        window.location.href = path;
      }
    },
  });

  const subRef = useRef<ReturnType<typeof supabase.auth.onAuthStateChange> | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const lastEmitted = useRef<{
    ready: boolean;
    loading: boolean;
    initializing: boolean;
    userId: string | null;
    role: string | null;
    onboardingCompleted: boolean | null;
    verification: string | null;
    isAdminUser: boolean;
    email: string | null;
  } | null>(null);

  const shallowDifferent = (a: typeof lastEmitted.current, b: typeof lastEmitted.current) => {
    if (!a || !b) return true;
    return (
      a.ready !== b.ready ||
      a.loading !== b.loading ||
      a.initializing !== b.initializing ||
      a.userId !== b.userId ||
      a.role !== b.role ||
      a.onboardingCompleted !== b.onboardingCompleted ||
      a.verification !== b.verification ||
      a.isAdminUser !== b.isAdminUser ||
      a.email !== b.email
    );
  };

  const emitIfChanged = (next: AuthState) => {
    const snapshot = {
      ready: next.ready,
      loading: next.loading,
      initializing: next.initializing,
      userId: next.user?.id ?? null,
      role: next.profile?.role ?? null,
      onboardingCompleted: next.profile?.onboarding_completed ?? null,
      verification: next.profile?.verification_status ?? null,
      isAdminUser: next.isAdminUser,
      email: next.email,
    };
    if (shallowDifferent(lastEmitted.current, snapshot)) {
      lastEmitted.current = snapshot;
      
      // Update user ID for logging
      setCurrentUserId(next.user?.id || null);
      
      setState(next);
    } else {
      // No-op: identical; avoid re-render
    }
  };

  const compute = async (reason?: string, sessionOverride?: any) => {
    if (inFlight.current) {
      await inFlight.current; // serialize; don't overlap
      return;
    }
    inFlight.current = (async () => {
      try {
        const [{ data: s }, { data: u }] = await Promise.all([
          supabase.auth.getSession(),
          supabase.auth.getUser(),
        ]);

        const user = u?.user ?? s?.session?.user ?? null;
        let profile: AuthState["profile"] = null;
        let isAdminUser = false;
        const email = user?.email ?? null;

        if (user?.id) {
          const { data: p } = await supabase
            .from("profiles")
            .select("role,onboarding_completed,verification_status,verification_status_new,profile_status,full_name")
            .eq("user_id", user.id)
            .maybeSingle();
          profile = p ?? null;

          // Check admin status - query admin_users table directly
          try {
            const { data: adminCheck, error: adminError } = await supabase
              .from('admin_users')
              .select('user_id')
              .eq('user_id', user.id)
              .maybeSingle(); // <-- not .single()
            
            // Treat 0 rows (and 406) as "not admin" instead of blowing up
            isAdminUser = !!adminCheck;
            if (adminError && adminError.code !== 'PGRST116') { // optional: log non-406
              console.warn('[admin check] non-406 error:', adminError);
            }
            
            // If user is admin but has no profile, create a default admin profile
            if (isAdminUser && !profile) {
              profile = {
                role: 'admin',
                onboarding_completed: true,
                verification_status: 'approved',
                verification_status_new: 'approved',
                profile_status: 'verified_good',
                full_name: 'Admin User'
              };
            }
          } catch (error) {
            console.error('[AuthContext] Admin check failed:', error);
            isAdminUser = false;
          }
        }

        emitIfChanged({
          ready: true,
          loading: false,
          initializing: false, // <-- first auth check complete
          user,
          profile,
          isAdminUser,
          email,
          signOut: async () => {
            clearActing(); // Reset to default role on sign-out
            const path = await safeSignOut(false);
            if (path) {
              window.location.href = path;
            }
          },
        });
      } catch (e) {
        emitIfChanged({
          ready: true,
          loading: false,
          initializing: false, // <-- first auth check complete (even on error)
          user: null,
          profile: null,
          isAdminUser: false,
          email: null,
          signOut: async () => {
            clearActing(); // Reset to default role on sign-out
            const path = await safeSignOut(false);
            if (path) {
              window.location.href = path;
            }
          },
        });
      } finally {
        inFlight.current = null;
      }
    })();
  };

  useEffect(() => {
    if (_authSubBound) return; // prevent duplicate subscriptions
    _authSubBound = true;

    // Initial compute
    compute("initial");
    
    // Subscribe to auth events - only meaningful ones
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // Only recompute on meaningful events
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED" || event === "INITIAL_SESSION") {
        compute(`event:${event}`);
      }
    });

    subRef.current = { data: { subscription } } as any;

    return () => {
      subscription?.unsubscribe?.();
      subRef.current = null;
      _authSubBound = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => state, [state.ready, state.loading, state.user, state.profile?.role, state.profile?.onboarding_completed, state.profile?.verification_status, state.profile?.verification_status_new, state.profile?.profile_status, state.isAdminUser, state.email]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
